<!--
  Finding.refs comes from the check's own source rather than from the target
  being scanned, so it is the one string family in the schema that is safe to
  turn into a link. Even then, the scheme is checked before it is used: an
  https or http URL becomes an anchor, anything else stays plain text.
-->
<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  url: string
}>()

const ALLOWED_PROTOCOLS = new Set(['https:', 'http:'])
const LABEL_MAX = 60

interface ParsedRef {
  readonly href: string
  readonly label: string
}

const parsed = computed<ParsedRef | null>(() => {
  let target: URL
  try {
    target = new URL(props.url)
  } catch {
    return null
  }
  if (!ALLOWED_PROTOCOLS.has(target.protocol)) {
    return null
  }
  const full = `${target.host}${target.pathname}`
  const label = full.length > LABEL_MAX ? `${full.slice(0, LABEL_MAX)}…` : full
  return { href: target.href, label }
})
</script>

<template>
  <a
    v-if="parsed"
    class="tap inline-flex items-center gap-1 text-accent"
    :href="parsed.href"
    :title="props.url"
    target="_blank"
    rel="noopener noreferrer nofollow ugc"
  >
    <span>{{ parsed.label }}</span>
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 10 L10 6 M7 6 H10 V9" />
    </svg>
    <span class="sr-only">(opens in a new tab)</span>
  </a>
  <span
    v-else
    class="quoted"
  >{{ props.url }}</span>
</template>
